import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { getGlobalSyncLogs, retrySyncLog } from "@/services/channelCenter";
import type { SyncLogStatus } from "@/services/types/channelCenter";
import { relativeTime } from "./channelCenterConstants";
import { SyncAttemptsRow, SyncAttemptsToggle } from "./SyncAttemptsRow";

function Pill({ tone, children }: { tone: "green" | "amber" | "gray" | "red"; children: React.ReactNode }) {
  const cls = {
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    gray: "bg-slate-100 text-slate-600",
    red: "bg-rose-50 text-rose-600",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${cls}`}>
      <span className="h-[7px] w-[7px] rounded-full bg-current" />
      {children}
    </span>
  );
}

const STATUS_TONE: Record<SyncLogStatus, "green" | "amber" | "gray" | "red"> = {
  sent: "green",
  pending: "amber",
  skipped: "gray",
  superseded: "gray",
  failed: "red",
  dead: "gray",
};

const FILTERS: { key: string; label: string; status?: SyncLogStatus }[] = [
  { key: "all", label: "全部" },
  { key: "sent", label: "成功", status: "sent" },
  { key: "failed", label: "失败·待重试", status: "failed" },
  { key: "dead", label: "死信 dead", status: "dead" },
];

export function GlobalSyncLogPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [openId, setOpenId] = useState<string | null>(null);   // GYG-LOG-1 · 展开看逐次请求 / 返回的那条

  const logsQ = useQuery({
    queryKey: ["channel-center", "global-logs", statusFilter],
    queryFn: () => getGlobalSyncLogs({ status: statusFilter === "all" ? undefined : statusFilter, pageSize: 100 }),
  });

  const retryMut = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) => retrySyncLog(id, force),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["channel-center", "global-logs"] }),
  });

  function handleRetry(id: string, status: string) {
    const force = status === "dead";
    const msg = force ? "死信事件重试需要二次确认——确认要重新推送这条已达上限的事件？" : "确认重试这条失败事件？";
    if (!window.confirm(msg)) return;
    retryMut.mutate({ id, force });
  }

  const logs = logsQ.data ?? [];

  return (
    <div className="flex flex-col gap-4 p-6 lg:p-8">
      <button type="button" onClick={() => navigate("/admin/api-integration")} className="flex w-fit items-center gap-1.5 text-[13px] font-medium text-blue-600 hover:underline">
        <ArrowLeft size={14} /> 返回平台列表
      </button>

      <div className="rounded-[14px] border border-slate-200 bg-white p-5">
        <h3 className="text-[15px] font-semibold text-slate-900">全局同步日志</h3>
        <div className="mb-3.5 mt-1 text-[12px] text-slate-400">全平台出站事件 · 失败行可直接重试</div>

        <div className="mb-3 flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={`rounded-md px-3 py-1.5 text-[12px] font-medium transition ${
                statusFilter === f.key ? "bg-blue-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {logsQ.isLoading ? (
          <div className="p-8 text-center text-[13px] text-slate-500">加载中...</div>
        ) : logsQ.isError ? (
          <button type="button" onClick={() => logsQ.refetch()} className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700 hover:bg-rose-100">
            加载失败，点击重试
          </button>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center text-[13px] text-slate-400">暂无记录</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[12px] font-medium text-slate-400">
                <th className="py-2">时间</th><th>平台</th><th>类型</th><th>方向</th><th>数量</th><th>状态</th><th>耗时</th><th>请求 / 返回</th><th></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <Fragment key={l.id}>
                <tr className="border-b border-slate-100 last:border-0">
                  <td className="py-2.5 tabular-nums">{relativeTime(l.created_at)}</td>
                  <td className="uppercase">{l.platform_code}</td>
                  <td>{l.event_type}</td>
                  <td>出站</td>
                  <td className="tabular-nums">{l.items_count ?? "—"}</td>
                  <td><Pill tone={STATUS_TONE[l.status]}>{l.status}</Pill></td>
                  <td className="tabular-nums">{l.duration_ms != null ? `${(l.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                  <td><SyncAttemptsToggle open={openId === l.id} onClick={() => setOpenId(openId === l.id ? null : l.id)} /></td>
                  <td>
                    {(l.status === "failed" || l.status === "dead") && (
                      <button
                        type="button"
                        disabled={retryMut.isPending}
                        onClick={() => handleRetry(l.id, l.status)}
                        className="rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        重试
                      </button>
                    )}
                  </td>
                </tr>
                {openId === l.id && <SyncAttemptsRow eventId={l.id} colSpan={9} />}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
